import { remToPx, stripUnit } from 'polished';
import { useMemo } from 'react';
import type { Layout } from 'react-grid-layout';

import { useTheme } from '../../../hooks';

/** A breakpoint on the responsive GridLayout editor */
export type GridBreakPoint = 'lg' | 'md' | 'sm' | 'xs' | 'xxs';

/** The persisted layout model could contain any subset of breakpoints */
export type GridLayoutModel = Partial<Record<GridBreakPoint, Layout[]>>;

/** Numeric configuration per each breakpoint in GridLayout */
export type GridBreakPointWiseValues = Partial<Record<GridBreakPoint, number>>;

/**
 * Config common to GridLayoutEditor and GridLayoutViewer.
 * These properties need to stay consistent between the design-time and run-time layouts.
 * @returns Default RGL props for the GridLayout editor/viewer
 */
export default function useGridLayoutConfig(padContent: boolean) {
  const theme = useTheme();
  return useMemo(() => {
    // Determine Gap around different grid cells (px)
    let gap: number;
    try {
      // Just for safety in case the value is not in rem etc.
      // `polished` throws errors in those scenarios
      // Shouldn't normally needed since Cosmos values are always in `rem`s
      gap = 2 * Number(stripUnit(remToPx(theme.base.spacing)));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
      gap = 16;
    }

    return {
      margin: [gap, gap] as [number, number],
      containerPadding: [padContent ? gap : 0, 0] as [number, number],
      compactType: 'vertical' as const
    };
  }, [theme, padContent]);
}
